import { useState, useEffect, useCallback, useRef } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router'
import { countryState, useTranslate } from '@repo/unicity'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ActionButton } from '@/core/components/Button/action-button.component'
import { DynamicCreditCardIcons } from '@/core/components/CreditCard/dynamic-creditcard-icons.component'
import { ErrorMessages } from '@/core/components/ErrorBox/error-messages.component'
import { getCardIssuingNetwork } from '@/core/utils'
import { ReCaptchaV2Modal } from '@/core/recaptcha/recaptcha-v2-modal.component'
import { useRecaptchaGate } from '@/core/recaptcha/use-recaptcha-gate.hook'
import BinIcon from '@/core/assets/BinIcon.svg'
import CreditCardIcon from '@/core/assets/creditcard-icons/CreditCardIcon.svg?react'
import BankwireIcon from '@/core/assets/creditcard-icons/BankwireIcon.svg?react'
import PayAtPickupIcon from '@/core/assets/creditcard-icons/PayAtPickupIcon.svg?react'
import WarningIcon from '../assets/WarningIcon.svg?react'
import {
    omanSelectedPaymentMethodAtom,
    omanAvailablePaymentMethodsAtom,
    omanFormatDataAtom,
    omanSelectedShippingMethodAtom,
    omanDuplicateOrderAtom,
    omanMemberShippingFormAtom,
    omanCartHasSubscriptionAtom
} from '../../../stores/oman.store'
import { omanConfig } from '../../../oman.config'
import { useOmanPayment } from '../../../hooks/useOmanPayment'
import { PAYMENTS_V3_METHOD, type TOmanPaymentMethod, type TOmanCreditCardForm, type TOmanCreditcardAlias } from '../../../types/oman.types'
import AddCreditCard from './AddCreditCard'
import ProcessingPaymentModal from './ProcessingPaymentModal'
import DuplicateOrderDialog from './DuplicateOrderDialog'

// ─── Payment option labels ─────────────────────────────────────────────────────
const METHOD_LABEL_KEYS: Record<TOmanPaymentMethod, string> = {
    // 'credit_card' not 'new_credit_card': Oman uses 3DS and does not store the
    // card in Unicity, so the label must not imply a new saved card (USHOP-3078).
    creditcard: 'credit_card',
    creditCardAlias: 'saved_card',
    bankwire: 'bank_wire',
    cashOnDelivery: 'cash_on_delivery',
    pickupPayment: 'pickup_payment'
}

const METHOD_ICONS: Partial<Record<TOmanPaymentMethod, React.FC<React.SVGProps<SVGSVGElement>>>> = {
    creditcard: CreditCardIcon,
    bankwire: BankwireIcon,
    cashOnDelivery: PayAtPickupIcon,
    pickupPayment: PayAtPickupIcon
}

// ─── Saved card row ─────────────────────────────────────────────────────────────
const SavedCardRow = ({
    alias,
    selected,
    onSelect,
    onDelete
}: {
    alias: TOmanCreditcardAlias
    selected: boolean
    onSelect: () => void
    onDelete: () => void
}) => {
    const { methodDetails } = alias
    const last4 = methodDetails.creditCardNumberMasked?.slice(-4) ?? '****'
    const cardBrand = getCardIssuingNetwork(methodDetails.creditCardNumberMasked ?? '')

    return (
        <OptionCard selected={selected} onClick={onSelect} data-testid="payment-methods-box-option-card-on-select-1">
            <Stack direction="row" justifyContent="space-between" alignItems="center" data-testid="payment-methods-box-stack-radio-dot-1">
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0 }} data-testid="payment-methods-box-box-radio-dot-1">
                    <RadioDot checked={selected} data-testid="payment-methods-box-radio-dot-1" />
                    <Box sx={{ display: 'flex', alignItems: 'center', marginInlineStart: '24px' }} data-testid="payment-methods-box-box-credit-card-icon">
                        <CreditCardIcon width={24} height={24} data-testid="payment-methods-box-credit-card-icon" />
                    </Box>
                    <Typography
                        variant="body2"
                        sx={{ marginInlineStart: '12px', fontSize: 16, color: selected ? '#003764' : '#5A8FC3', fontWeight: selected ? 700 : 400 }}
                        data-testid="payment-methods-box-typography-1">
                        .... .... .... {last4}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" data-testid="payment-methods-box-stack-dynamic-credit-card-icons">
                    <DynamicCreditCardIcons paymentOptions={cardBrand} height={20} data-testid="payment-methods-box-dynamic-credit-card-icons-1" />
                    <Box
                        component="img"
                        src={BinIcon}
                        alt="Delete"
                        onClick={e => {
                            e.stopPropagation()
                            onDelete()
                        }}
                        sx={{ cursor: 'pointer', width: 24, height: 24, '&:hover': { opacity: 0.7 } }}
                        data-testid="payment-methods-box-box-1"
                    />
                </Stack>
            </Stack>
        </OptionCard>
    )
}

const RadioDot = ({ checked }: { checked: boolean }) => (
    <Box
        sx={{
            boxSizing: 'border-box',
            width: 20,
            height: 20,
            borderRadius: '50%',
            border: `2px solid ${checked ? '#003764' : '#c4d3e0'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
        }}
        data-testid="payment-methods-box-box-2">
        {checked && <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#003764' }} data-testid="payment-methods-box-box-3" />}
    </Box>
)

// ─── Bank wire details ──────────────────────────────────────────────────────────
const BankWireDetails = () => {
    const { translate } = useTranslate()
    const { bankWire } = omanConfig
    return (
        <Stack data-testid="payment-methods-box-stack-warning-icon-1">
            <Stack
                direction="row"
                gap="12px"
                sx={{
                    p: '12px',
                    borderRadius: '4px',
                    bgcolor: '#CCDEEF',
                    alignItems: 'flex-start'
                }}
                data-testid="payment-methods-box-stack-warning-icon-2">
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }} data-testid="payment-methods-box-box-warning-icon">
                    <WarningIcon data-testid="payment-methods-box-warning-icon" />
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 400, color: '#003764' }} data-testid="payment-methods-box-typography-title-bankwire-oman">
                    {translate('title_bankwire_oman')}
                </Typography>
            </Stack>
            <Stack sx={{ pt: '12px', gap: '20px' }} data-testid="payment-methods-box-stack-typography-bank-account-name-1">
                <Stack direction="row" gap="4px" data-testid="payment-methods-box-stack-typography-bank-account-name-2">
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#003764' }} data-testid="payment-methods-box-typography-bank-account-name">
                        {translate('bank_account_name')}:
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 400, color: '#003764' }} data-testid="payment-methods-box-typography-2">
                        {bankWire.accountName}
                    </Typography>
                </Stack>
                <Stack direction="row" gap="4px" data-testid="payment-methods-box-stack-typography-bank-account-number">
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#003764' }} data-testid="payment-methods-box-typography-bank-account-number">
                        {translate('bank_account_number')}:
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 400, color: '#003764' }} data-testid="payment-methods-box-typography-3">
                        {bankWire.accountNumber}
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    )
}

// ─── Payment option card ────────────────────────────────────────────────────────
const PaymentOptionCard = ({
    method,
    selected,
    onSelect,
    rightContent,
    children
}: {
    method: TOmanPaymentMethod
    selected: boolean
    onSelect: () => void
    rightContent?: React.ReactNode
    children?: React.ReactNode
}) => {
    const { translate } = useTranslate()
    return (
        <OptionCard selected={selected} onClick={onSelect} data-testid="payment-methods-box-option-card-on-select-2">
            <Stack
                sx={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 0,
                    '@media (max-width: 474px)': {
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: 1.5
                    }
                }}
                data-testid="payment-methods-box-stack-radio-dot-2">
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} data-testid="payment-methods-box-box-radio-dot-2">
                    <RadioDot checked={selected} data-testid="payment-methods-box-radio-dot-2" />
                    <Box sx={{ display: 'flex', alignItems: 'center', marginInlineStart: '24px' }} data-testid="payment-methods-box-label-group">
                        {METHOD_ICONS[method] &&
                            (() => {
                                const Icon = METHOD_ICONS[method]!
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }} data-testid="payment-methods-box-box-icon">
                                        <Icon width={24} height={24} data-testid="payment-methods-box-icon" />
                                    </Box>
                                )
                            })()}
                        <Typography
                            variant="body2"
                            sx={{ marginInlineStart: '12px', fontSize: 16, color: selected ? '#003764' : '#5A8FC3' }}
                            fontWeight={selected ? 700 : 400}
                            data-testid="payment-methods-box-typography-4">
                            {translate(METHOD_LABEL_KEYS[method])}
                        </Typography>
                    </Box>
                </Box>
                {rightContent && (
                    <Stack
                        sx={{
                            paddingInlineStart: 0,
                            '@media (max-width: 474px)': {
                                paddingInlineStart: '44px'
                            }
                        }}
                        data-testid="payment-methods-box-right-content-group">
                        {rightContent}
                    </Stack>
                )}
            </Stack>
            {selected && children && (
                <Box
                    mt={2}
                    sx={{
                        boxSizing: 'border-box',
                        width: '100%',
                        paddingInlineStart: { xs: 0, md: '44px' }
                    }}
                    onClick={e => e.stopPropagation()}
                    data-testid="payment-methods-box-box-4">
                    {children}
                </Box>
            )}
        </OptionCard>
    )
}

// ─── Main PaymentMethodsBox ─────────────────────────────────────────────────────
const PaymentMethodsBox = () => {
    const { translate } = useTranslate()
    const [selectedMethod, setSelectedMethod] = useAtom(omanSelectedPaymentMethodAtom)
    const availableMethods = useAtomValue(omanAvailablePaymentMethodsAtom)
    const formatData = useAtomValue(omanFormatDataAtom)
    const shippingMethod = useAtomValue(omanSelectedShippingMethodAtom)
    const memberShippingForm = useAtomValue(omanMemberShippingFormAtom)
    const hasSubscription = useAtomValue(omanCartHasSubscriptionAtom)
    const setDuplicateOrder = useSetAtom(omanDuplicateOrderAtom)
    const country = useAtomValue(countryState)
    const navigate = useNavigate()
    const { isVerifying: recaptchaVerifying, runWithRecaptcha, modalProps: recaptchaModalProps } = useRecaptchaGate()

    const {
        loading,
        errors,
        setErrors,
        setLoading,
        savedCards,
        submitCreditCardPayment,
        submitAliasPayment,
        submitBankWirePayment,
        submitCashPayment,
        deleteCard,
        checkDuplicate,
        closeProcessingPaymentModal,
        extractErrorMessage
    } = useOmanPayment()

    const [cardForm, setCardForm] = useState<TOmanCreditCardForm>({
        cardNumber: '',
        expMonth: '',
        expYear: '',
        cvv: '',
        saveCard: false,
        billingSameAsShipping: true,
        billing: { firstname: '', country: 'OM', address1: '', address2: '', city: '', state: '' }
    })
    const [selectedAliasId, setSelectedAliasId] = useState<string | null>(null)
    const [cardToDelete, setCardToDelete] = useState<TOmanCreditcardAlias | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        setSelectedMethod('creditcard')
        setSelectedAliasId(null)
        if (shippingMethod === 'pickup') {
            setCardForm(prev => ({ ...prev, billingSameAsShipping: false }))
        }
    }, [shippingMethod, setSelectedMethod])

    const selectedCountryCode = memberShippingForm.country || formatData?.country || 'OM'
    const filteredMethods = availableMethods.filter(m => {
        if (m === 'cashOnDelivery' && selectedCountryCode !== 'OM') return false
        return true
    })
    const optionMethods = filteredMethods.filter(method => method !== 'creditCardAlias')

    const hasRequiredCardFields = Boolean(cardForm.cardNumber.replace(/\D/g, '') && cardForm.expMonth && cardForm.expYear && cardForm.cvv)
    const hasRequiredBillingFields =
        cardForm.billingSameAsShipping && shippingMethod !== 'pickup'
            ? true
            : Boolean(
                  cardForm.billing.firstname.trim() &&
                  cardForm.billing.country.trim() &&
                  cardForm.billing.address1.trim() &&
                  cardForm.billing.city.trim() &&
                  cardForm.billing.state.trim()
              )
    const isCreditCardSubmitDisabled = selectedMethod === 'creditcard' && hasSubscription && (!hasRequiredCardFields || !hasRequiredBillingFields)
    const isAliasSubmitDisabled = selectedMethod === 'creditCardAlias' && !selectedAliasId
    const isSubmitting = loading || recaptchaVerifying

    const handleConfirmDelete = async () => {
        if (!cardToDelete) return
        setDeleting(true)
        try {
            const cardAlias = cardToDelete.methodDetails.alias
            if (selectedAliasId === cardAlias) {
                setSelectedAliasId(null)
                setSelectedMethod('creditcard')
            }
            await deleteCard(cardToDelete)
        } catch {
            setErrors([translate('failed_to_delete_card')])
        } finally {
            setDeleting(false)
            setCardToDelete(null)
        }
    }

    // Hard guard against a double-submit: the button disables on `loading`, but a
    // rapid second click can land before that state commits. A synchronous ref
    // closes that window so we never fire two payments (and bypass checkDuplicate).
    const submittingRef = useRef(false)

    const executePayment = useCallback(async () => {
        setLoading(true)
        setErrors([])
        try {
            switch (selectedMethod) {
                case 'creditcard':
                    await submitCreditCardPayment(cardForm)
                    return
                case 'creditCardAlias':
                    if (!selectedAliasId) {
                        setErrors([translate('please_select_a_saved_card')])
                        return
                    }
                    await submitAliasPayment(selectedAliasId)
                    return
                case 'bankwire':
                    await submitBankWirePayment()
                    return
                case 'cashOnDelivery':
                    await submitCashPayment(PAYMENTS_V3_METHOD.cod)
                    return
                case 'pickupPayment':
                    await submitCashPayment(PAYMENTS_V3_METHOD.payOnPickup)
                    return
            }
        } catch (err: unknown) {
            setErrors([extractErrorMessage(err)])
        } finally {
            setLoading(false)
        }
    }, [
        selectedMethod,
        cardForm,
        selectedAliasId,
        submitCreditCardPayment,
        submitAliasPayment,
        submitBankWirePayment,
        submitCashPayment,
        setLoading,
        setErrors,
        extractErrorMessage,
        translate
    ])

    const handleConfirmPayment = async () => {
        setErrors([])
        if (isCreditCardSubmitDisabled || isAliasSubmitDisabled) return
        if (selectedMethod === 'creditCardAlias' && !selectedAliasId) {
            setErrors([translate('please_select_a_saved_card')])
            return
        }
        if (submittingRef.current) return
        submittingRef.current = true

        try {
            await runWithRecaptcha(
                'checkout',
                async () => {
                    setLoading(true)
                    const dupResult = await checkDuplicate()
                    if (dupResult === 'duplicate') {
                        setLoading(false)
                        return
                    }
                    if (dupResult === 'error') {
                        window.location.replace('/')
                        return
                    }
                    setLoading(false)
                    await executePayment()
                },
                omanConfig.features.checkout.recaptcha
            )
        } finally {
            submittingRef.current = false
        }
    }

    // No per-section skeleton: payments.module.tsx owns the full-page
    // CheckoutLayoutSkeleton for any loading state, so by the time this
    // component mounts we are guaranteed to have formatData and available
    // methods. Early-exit on the remaining edge case (no available methods
    // for the current cart) to avoid rendering an empty list.
    if (!formatData || availableMethods.length === 0) return null

    return (
        <>
            <ReCaptchaV2Modal {...recaptchaModalProps} />
            <Box
                sx={{
                    boxSizing: 'border-box',
                    width: '100%',
                    background: '#ffffff',
                    borderRadius: '12px',
                    border: '1px solid #e0e0e0',
                    padding: { xs: '24px 20px', sm: '30px' }
                }}
                data-testid="payment-methods-box-box-typography-payment-method">
                <Stack spacing={1.5} data-testid="payment-methods-box-stack-typography-payment-method-1">
                    <Stack spacing={0.5} mb={1} data-testid="payment-methods-box-stack-typography-payment-method-2">
                        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#003764' }} data-testid="payment-methods-box-typography-payment-method">
                            {translate('payment_method')}
                        </Typography>
                        <Typography
                            sx={{ fontSize: 16, fontWeight: 400, color: '#003764' }}
                            data-testid="payment-methods-box-typography-please-choose-your-payment-method">
                            {translate('please_choose_your_payment_method')}
                        </Typography>
                    </Stack>

                    {optionMethods.includes('creditcard') && (
                        <PaymentOptionCard
                            method="creditcard"
                            selected={selectedMethod === 'creditcard'}
                            onSelect={() => {
                                setErrors([])
                                setSelectedMethod('creditcard')
                                setSelectedAliasId(null)
                            }}
                            rightContent={
                                selectedMethod === 'creditcard' ? (
                                    <DynamicCreditCardIcons
                                        paymentOptions={country.payment_options}
                                        data-testid="payment-methods-box-dynamic-credit-card-icons-2"
                                    />
                                ) : undefined
                            }
                            data-testid="payment-methods-box-payment-option-card-1">
                            {hasSubscription && (
                                <AddCreditCard value={cardForm} onChange={setCardForm} data-testid="payment-methods-box-add-credit-card-set-card-form" />
                            )}
                        </PaymentOptionCard>
                    )}

                    {hasSubscription &&
                        savedCards.map(alias => (
                            <SavedCardRow
                                key={alias.methodDetails.alias}
                                alias={alias}
                                selected={selectedAliasId === alias.methodDetails.alias && selectedMethod === 'creditCardAlias'}
                                onSelect={() => {
                                    setErrors([])
                                    setSelectedAliasId(alias.methodDetails.alias)
                                    setSelectedMethod('creditCardAlias')
                                }}
                                onDelete={() => setCardToDelete(alias)}
                                data-testid="payment-methods-box-saved-card-row"
                            />
                        ))}

                    {optionMethods
                        .filter(m => m !== 'creditcard')
                        .map(method => (
                            <PaymentOptionCard
                                key={method}
                                method={method}
                                selected={selectedMethod === method}
                                onSelect={() => {
                                    setErrors([])
                                    setSelectedMethod(method)
                                    setSelectedAliasId(null)
                                }}
                                data-testid="payment-methods-box-payment-option-card-2">
                                {method === 'bankwire' && <BankWireDetails data-testid="payment-methods-box-bank-wire-details" />}
                            </PaymentOptionCard>
                        ))}

                    {errors.length > 0 && <ErrorMessages list={errors} data-testid="payment-methods-box-error-messages" />}

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent={{ xs: 'center', md: 'space-between' }}
                        alignItems="center"
                        spacing={{ xs: 2, md: 0 }}
                        sx={{ mt: '16px !important', width: '100%' }}
                        data-testid="payment-methods-box-stack-action-button-handle-confirm-payment">
                        <ActionButton
                            text={isSubmitting ? '' : translate('confirm_payment')}
                            onClick={handleConfirmPayment}
                            disabled={isSubmitting || isCreditCardSubmitDisabled || isAliasSubmitDisabled}
                            startIcon={
                                isSubmitting ? <CircularProgress size={22} color="inherit" data-testid="payment-methods-box-circular-progress-1" /> : undefined
                            }
                            data-testid="payment-methods-box-action-button-handle-confirm-payment"
                        />
                        <Typography
                            onClick={() => navigate(-1)}
                            sx={{
                                fontSize: 16,
                                fontWeight: 400,
                                color: '#003B6F',
                                cursor: 'pointer',
                                '&:hover': { color: '#003764' }
                            }}
                            data-testid="payment-methods-box-typography-cancel">
                            {translate('cancel')}
                        </Typography>
                    </Stack>

                    {/* Delete card confirmation */}
                    <Dialog
                        open={!!cardToDelete}
                        onClose={() => !deleting && setCardToDelete(null)}
                        PaperProps={{ sx: { borderRadius: 3, px: 1 } }}
                        data-testid="payment-methods-box-dialog">
                        <DialogTitle sx={{ fontWeight: 700, color: '#003764' }} data-testid="payment-methods-box-dialog-title-delete-saved-card">
                            {translate('delete_saved_card')}
                        </DialogTitle>
                        <DialogContent data-testid="payment-methods-box-dialog-content">
                            <DialogContentText data-testid="payment-methods-box-dialog-content-text-are-you-sure-delete-card">
                                {translate('are_you_sure_delete_card')}{' '}
                                <strong data-testid="payment-methods-box-strong">
                                    {cardToDelete?.methodDetails.creditCardNumberMasked?.slice(-4) ?? '****'}
                                </strong>
                                ?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }} data-testid="payment-methods-box-dialog-actions">
                            <Button
                                onClick={() => setCardToDelete(null)}
                                disabled={deleting}
                                sx={{ color: '#5A8FC3' }}
                                data-testid="payment-methods-box-button-cancel">
                                {translate('cancel')}
                            </Button>
                            <Button
                                onClick={handleConfirmDelete}
                                variant="contained"
                                disabled={deleting}
                                color="error"
                                startIcon={
                                    deleting ? <CircularProgress size={16} color="inherit" data-testid="payment-methods-box-circular-progress-2" /> : undefined
                                }
                                data-testid="payment-methods-box-button-handle-confirm-delete">
                                {translate('delete')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Duplicate order dialog */}
                    <DuplicateOrderDialog
                        onProceed={executePayment}
                        onCancel={() => {
                            setDuplicateOrder(null)
                            setLoading(false)
                        }}
                        data-testid="payment-methods-box-duplicate-order-dialog"
                    />

                    {/* Processing modal */}
                    <ProcessingPaymentModal onClose={closeProcessingPaymentModal} data-testid="payment-methods-box-processing-payment-modal" />
                </Stack>
            </Box>
        </>
    )
}

const OptionCard = styled(Box, {
    shouldForwardProp: p => p !== 'selected'
})<{ selected: boolean }>`
    border: ${({ selected }) => (selected ? '2px solid #99BCDF' : '1px solid #CCDEEF')};
    border-radius: 8px;
    padding: 15px 16px;
    cursor: pointer;
    background: ${({ selected }) => (selected ? '#F4F8FB' : '#FFFFFF')};
    transition: border-color 0.2s;

    @media screen and (min-width: 600px) {
        padding: 15px 25px;
    }
`

export default PaymentMethodsBox
